/**
 * The trim view (1.7.2): while a clip is slipped or slid on the timeline, the sequence viewer shows the frames at the
 * edit points instead of the playhead's picture. Slip: the previous clip's last frame (small), the clip's new first
 * and last frames (large) and the next clip's first frame (small), with the offset and the new source timecodes.
 * Slide: the previous clip's new last frame, the clip's first and last frames and the next clip's new first frame.
 * Layer and A2 clips show the clip's own two frames (A2 has no picture: its timecodes only).
 *
 * The timeline writes the preview to a small store (no editor re-render on every pointer move); the view reads it.
 * Frames come from <video> elements on the sources' proxies, with seeks coalesced: while one seek runs only the
 * latest wanted time is kept, and it is sent when the element has seeked.
 */
import { useEffect, useRef, useSyncExternalStore, type CSSProperties } from 'react';
import { TICKS_PER_SECOND, formatTimecode, framesToTimecode, ticksPerFrame, ticksToFrames, type Rational, type SequenceDocument } from '@prism/contracts';
import type { SequenceSource } from '../../api/hooks';
import { rollFrames, signedTimecode, trimFrames, type TrimFrame } from './timelineTools';

/** A slip or slide in progress: the document before the drag and as the drag would leave it. */
export interface TrimPreview {
  /** 1.7.4: a roll (⇧B) shows the outgoing clip's new last frame and the incoming clip's new first frame. */
  mode: 'slip' | 'slide' | 'roll';
  clipId: string;
  before: SequenceDocument;
  after: SequenceDocument;
}

export interface TrimPreviewStore {
  get(): TrimPreview | null;
  set(p: TrimPreview | null): void;
  subscribe(fn: () => void): () => void;
}

export function createTrimPreviewStore(): TrimPreviewStore {
  let value: TrimPreview | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(p) {
      if (p === value) return;
      value = p;
      for (const fn of listeners) fn();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
}

const tcOf = (ticks: number, rate: Rational) => formatTimecode(framesToTimecode(ticksToFrames(Math.max(0, ticks), rate), rate));

/** One frame of a source, seeked to the middle of the frame (so rounding never shows the one before). */
function FrameVideo({ url, sourceTicks, rate }: { url: string; sourceTicks: number; rate: Rational }) {
  const ref = useRef<HTMLVideoElement>(null);
  const seek = useRef<{ busy: boolean; want: number | null }>({ busy: false, want: null });
  const seconds = (sourceTicks + ticksPerFrame(rate) / 2) / TICKS_PER_SECOND;
  const go = () => {
    const v = ref.current;
    const s = seek.current;
    if (!v || s.want === null || s.busy || v.readyState < 1) return;
    const t = s.want;
    s.want = null;
    if (Math.abs(v.currentTime - t) < 1e-4) return;
    s.busy = true;
    v.currentTime = t;
  };
  useEffect(() => { seek.current.want = seconds; go(); });
  return (
    <video ref={ref} src={url} muted playsInline preload="auto" crossOrigin="anonymous"
      onLoadedMetadata={go} onSeeked={() => { seek.current.busy = false; go(); }} />
  );
}

const CAPTIONS: Record<TrimFrame['role'], string> = { 'prev-out': 'Previous out', 'clip-in': 'In', 'clip-out': 'Out', 'next-in': 'Next in' };

export function TrimView({ store, sources }: { store: TrimPreviewStore; sources: Map<string, SequenceSource> }) {
  const preview = useSyncExternalStore(store.subscribe, store.get);
  if (!preview) return null;
  const shown = preview.mode === 'roll'
    ? (() => { const r = rollFrames(preview.before, preview.after, preview.clipId); return r ? { ...r, offsetTicks: r.moveTicks } : null; })()
    : trimFrames(preview.before, preview.after, preview.clipId);
  if (!shown) return null;
  const rate = preview.after.frameRate;
  const audioOnly = shown.track === 'audio';
  return (
    <div className="trim-view" data-testid="trim-view" data-mode={preview.mode} data-track={shown.track} aria-live="polite">
      <div className="trim-view-frames" style={{ '--trim-ar': `${preview.after.width} / ${preview.after.height}` } as CSSProperties}>
        {shown.frames.map((f) => {
          const src = sources.get(f.versionId);
          const large = preview.mode === 'roll' || f.role === 'clip-in' || f.role === 'clip-out';
          const url = src?.proxyUrl ?? null;
          return (
            <figure key={f.role} className={`trim-frame ${large ? 'large' : 'small'}`} data-testid={`trim-frame-${f.role}`} data-source-ticks={f.sourceTicks}>
              <div className="trim-frame-picture" style={{ aspectRatio: `${preview.after.width} / ${preview.after.height}` }}>
                {audioOnly || src?.mediaKind === 'audio' ? <span className="trim-frame-empty">Sound</span>
                  : !url ? <span className="trim-frame-empty">{src?.live ? 'Recording' : 'No preview'}</span>
                  : src?.mediaKind === 'image' ? <img src={url} alt="" />
                  : <FrameVideo url={url} sourceTicks={f.sourceTicks} rate={rate} />}
              </div>
              <figcaption><span>{CAPTIONS[f.role]}</span> <span className="mono">{tcOf(f.sourceTicks, rate)}</span></figcaption>
            </figure>
          );
        })}
      </div>
      <div className="trim-view-readout mono" data-testid="trim-readout">
        {preview.mode === 'slip' ? <>Slip {signedTimecode(shown.offsetTicks, rate)}</> : preview.mode === 'roll' ? <>Roll {signedTimecode(shown.moveTicks, rate)}</> : <>Slide {signedTimecode(shown.moveTicks, rate)}</>}
      </div>
    </div>
  );
}
